import React, { useEffect, useState } from 'react';

import { ApolloError } from '@apollo/client';
import { IDBAsset } from '@netfront/common-library';
import { ButtonIconOnly, CheckboxGroup, ICheckboxItem, IMessage } from '@netfront/ui-library';

import { DEFAULT_API_ERROR_MESSAGE, DEFAULT_MESSAGE } from '../QuestionSnippet.constants';
import { handleApolloError } from '../QuestionSnippet.handlers';
import { saveContentSnippetQuestionAndApplyQuestionActions } from '../QuestionSnippet.helpers';

import { QuestionAnswerCheckboxSnippetProps } from './QuestionAnswerCheckboxSnippet.interfaces';

import {
  CONTENT_PAGE_CONTEXT_GRAPHQL,
  getContentSnippet,
  getContentSnippetFormId,
  ISetUserAnswerCheckboxMutationVariables,
  ISetUserAnswerMutationGraphQLResponse,
  useContentPageContext,
} from '../../../../contexts';
import { useDeleteUserGeneratedResponse, useEkardoMutation } from '../../../../hooks';
import {
  IContentPage,
  IDBQuestionResponse,
  IQuestionAnswerCheckbox,
  IQuestionAnswerCheckboxResponseCheckbox,
  IQuestionConfigurationCheckbox,
} from '../../../../interfaces';
import { IUserGeneratedResponseOnSaveCompletedResponse, UserGeneratedResponse } from '../../../UserGeneratedResponse';
import { getMutationBaseVariables } from '../../Snippets.helpers';

const QuestionAnswerCheckboxSnippet = ({
  accessToken,
  contentSnippetId,
  contentSnippetQuestion,
  message = DEFAULT_MESSAGE,
  userFlowStepTrackId,
  placeholders,
}: QuestionAnswerCheckboxSnippetProps) => {
  const { dispatch, state: contentPage } = useContentPageContext();
  const { actions, id: contentPageId } = contentPage as IContentPage;

  const contentSnippet = contentSnippetQuestion ?? getContentSnippet(contentSnippetId, contentPage);
  const contentSnippetFormId = getContentSnippetFormId(contentSnippetId, contentPage);

  const { answer, configuration, message: contentSnippetMessage, mandatory: isMandatory = false } = contentSnippet ?? {};
  const { selectedAnswers = [] } = answer ?? ({} as IQuestionAnswerCheckbox);
  const {
    allowUserGeneratedResponses: shouldAllowUserGeneratedResponses,
    responseSet: { availableResponses, id: questionResponseSetId, title = '' },
  } = configuration ?? ({} as IQuestionConfigurationCheckbox);

  const [checkboxItems, setCheckboxItems] = useState<ICheckboxItem[]>([]);
  const [currentMessage, setCurrentMessage] = useState<IMessage>(message);
  const [deletedQuestionResponseCheckboxId, setDeletedQuestionResponseCheckboxId] = useState<number>();
  const [savedAvailableResponses, setSavedAvailableResponses] = useState<IDBQuestionResponse[]>(availableResponses);
  const [savedQuestionResponseCheckboxIds, setSavedQuestionResponseCheckboxIds] = useState<number[]>(
    selectedAnswers.map((selectedAnswer) => selectedAnswer.questionResponseCheckboxId)
  );
  const [savedSelectedAnswers, setSavedSelectedAnswers] = useState<IQuestionAnswerCheckboxResponseCheckbox[]>(selectedAnswers);

  const [executeSetUserAnswerCheckbox] = useEkardoMutation<ISetUserAnswerMutationGraphQLResponse, ISetUserAnswerCheckboxMutationVariables>({
    mutation: CONTENT_PAGE_CONTEXT_GRAPHQL.mutations.setUserAnswerCheckbox,
    options: {
      onCompleted: (data) => {
        setCurrentMessage(DEFAULT_MESSAGE);
        const {
          questionAnswer: {
            setUserAnswer: { id: questionAnswerCheckboxId },
          },
        } = data;

        const updatedSelectedAnswers: IQuestionAnswerCheckboxResponseCheckbox[] = [
          ...savedQuestionResponseCheckboxIds.map(
            (savedQuestionResponseCheckboxId) =>
              ({
                questionAnswerCheckboxId,
                questionResponseCheckboxId: savedQuestionResponseCheckboxId,
              } as IQuestionAnswerCheckboxResponseCheckbox)
          ),
        ];

        setSavedSelectedAnswers(updatedSelectedAnswers);

        saveContentSnippetQuestionAndApplyQuestionActions({
          actions,
          answer,
          availableResponses: savedAvailableResponses,
          contentPage,
          contentSnippetId,
          dispatch,
          selectedQuestionAnswers: {
            checkboxSelectedAnswers: updatedSelectedAnswers,
          },
        });
      },
      onError: (error) => {
        handleApolloError(error);
        setCurrentMessage({
          error: DEFAULT_API_ERROR_MESSAGE,
          success: '',
        });
      },
    },
    token: accessToken,
  });

  const handleAddUserGeneratedResponseSaveCompleted = (data: IUserGeneratedResponseOnSaveCompletedResponse) => {
    if (!contentSnippetFormId) {
      return;
    }

    const { updatedAvailableResponses } = data;

    const currentQuestionResponseIds = availableResponses.map(({ id }) => id);

    setSavedAvailableResponses(updatedAvailableResponses);

    const newAvailableResponseIds = updatedAvailableResponses
      .filter(({ id }) => !currentQuestionResponseIds.includes(id))
      .map(({ id }) => id);

    const allSavedQuestionResponseCheckboxIds = [...savedQuestionResponseCheckboxIds, ...newAvailableResponseIds];

    setSavedQuestionResponseCheckboxIds(allSavedQuestionResponseCheckboxIds);

    const baseVariables = getMutationBaseVariables(contentPageId, contentSnippetFormId, contentSnippetId, Number(userFlowStepTrackId));

    executeSetUserAnswerCheckbox({
      variables: {
        ...baseVariables,
        selectedAnswers: allSavedQuestionResponseCheckboxIds.map((questionResponseCheckboxId) => ({
          questionResponseCheckboxId,
        })),
      },
    });
  };

  const handleCheckboxChange = (inputValues: string[]) => {
    if (!contentSnippetFormId) {
      return;
    }

    const convertedQuestionResponseCheckboxIds = inputValues.map((inputValue) => Number(inputValue));

    setSavedQuestionResponseCheckboxIds(convertedQuestionResponseCheckboxIds);

    const baseVariables = getMutationBaseVariables(contentPageId, contentSnippetFormId, contentSnippetId, Number(userFlowStepTrackId));

    executeSetUserAnswerCheckbox({
      variables: {
        ...baseVariables,
        selectedAnswers: [
          ...convertedQuestionResponseCheckboxIds.map((convertedQuestionResponseCheckboxId) => ({
            questionResponseCheckboxId: convertedQuestionResponseCheckboxId,
          })),
        ],
      },
    });
  };

  const handleDeleteUserGeneratedResponseCompleted = () => {
    const updatedSavedAvailableResponses = savedAvailableResponses.filter(({ id }) => id !== deletedQuestionResponseCheckboxId);
    const updatedSavedQuestionResponseCheckboxIds = savedQuestionResponseCheckboxIds.filter(
      (savedQuestionResponseCheckboxId) => savedQuestionResponseCheckboxId !== deletedQuestionResponseCheckboxId
    );
    const updatedSelectedAnswers = savedSelectedAnswers.filter(
      ({ questionResponseCheckboxId }) => questionResponseCheckboxId !== deletedQuestionResponseCheckboxId
    );

    setSavedAvailableResponses(updatedSavedAvailableResponses);
    setSavedQuestionResponseCheckboxIds(updatedSavedQuestionResponseCheckboxIds);
    setSavedSelectedAnswers(updatedSelectedAnswers);

    saveContentSnippetQuestionAndApplyQuestionActions({
      actions,
      answer,
      availableResponses: updatedSavedAvailableResponses,
      contentPage,
      contentSnippetId,
      dispatch,
      selectedQuestionAnswers: {
        checkboxSelectedAnswers: updatedSelectedAnswers,
      },
    });
  };

  const handleDeleteUserGeneratedResponseError = (error: ApolloError) => {
    handleApolloError(error);
    setCurrentMessage({
      error: DEFAULT_API_ERROR_MESSAGE,
      success: '',
    });
  };

  const { handleDeleteUserGeneratedResponse } = useDeleteUserGeneratedResponse({
    onCompleted: handleDeleteUserGeneratedResponseCompleted,
    onError: handleDeleteUserGeneratedResponseError,
  });

  useEffect(() => {
    if (!savedSelectedAnswers.length) {
      return;
    }

    saveContentSnippetQuestionAndApplyQuestionActions({
      actions,
      answer,
      contentPage,
      contentSnippetId,
      dispatch,
      selectedQuestionAnswers: {
        checkboxSelectedAnswers: savedSelectedAnswers,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkboxItemsPromises = savedAvailableResponses
      .filter(({ id }) => (contentSnippetQuestion ? savedQuestionResponseCheckboxIds.includes(id) : true))
      .map(async (availableResponse): Promise<ICheckboxItem> => {
        const { asset, id, label = '', userId } = availableResponse;
        const { presignedUrl } = asset ?? ({} as IDBAsset);

        let updatedText = label;

        if (placeholders) {
          updatedText = Object.keys(placeholders).reduce((accumulator, placeholder) => {
            if (!accumulator.includes(placeholder)) {
              return accumulator;
            }

            return accumulator.replaceAll(placeholder, placeholders[placeholder]);
          }, label);
        }

        const idAsString = id.toString();

        const deleteButton = userId ? (
          <div className="checkbox-item-delete-button-container">
            <ButtonIconOnly
              iconId="id_bin_icon"
              text="Delete"
              onClick={() => {
                setDeletedQuestionResponseCheckboxId(id);

                handleDeleteUserGeneratedResponse({
                  questionResponseId: id,
                  questionResponseSetId,
                });
              }}
            />
          </div>
        ) : null;

        return {
          deleteButton,
          id: `checkbox-${contentSnippetId.toString()}-${idAsString}`,
          imageSrc: presignedUrl,
          labelText: updatedText,
          value: idAsString,
        };
      });

    const loadCheckboxItems = async () => {
      setCheckboxItems(await Promise.all(checkboxItemsPromises));
    };

    loadCheckboxItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAvailableResponses]);

  useEffect(() => {
    if (!contentSnippetMessage) {
      return;
    }

    setCurrentMessage(contentSnippetMessage);
  }, [contentSnippetMessage]);

  const isSummary = Boolean(contentSnippetQuestion);

  return (
    <React.Fragment>
      <CheckboxGroup
        errorMessage={currentMessage.error}
        isDisabled={isSummary}
        isRequired={isMandatory}
        items={checkboxItems}
        legendText={title}
        name={`checkbox-group-${contentSnippetId}`}
        values={savedQuestionResponseCheckboxIds.map((savedQuestionResponseCheckboxId) => String(savedQuestionResponseCheckboxId))}
        isLabelHidden
        onChange={(values) => handleCheckboxChange(values)}
      />
      {contentSnippet && !isSummary && shouldAllowUserGeneratedResponses && (
        <UserGeneratedResponse
          accessToken={accessToken}
          contentSnippetId={contentSnippet.id}
          currentAvailableResponses={availableResponses}
          questionConfigurationType="Checkbox"
          questionResponseSetId={questionResponseSetId}
          onSaveCompleted={handleAddUserGeneratedResponseSaveCompleted}
        />
      )}
    </React.Fragment>
  );
};

export { QuestionAnswerCheckboxSnippet };
